import { useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import { ChevronDown } from 'ui/icons/navigation/ChevronDown';
import { DateRangePicker } from 'ui/datePicker';
import { RELATIVE_DATE_PRESETS_WITH_ALL_TIME, ALL_TIME, CUSTOM_RANGE } from 'ui/datePicker/helpers';
import { DateType } from 'ui/datePicker/types';
import { guessCurrentTimeZone } from 'utils/timeZone';

import { useEventFilterContext } from '../contexts/filter';
import { DatePickerContainer, DateTriggerButton, DatePickerWrapper, TimeText } from './styled';

interface EventDatePickerProps {
  isDisabled?: boolean;
}

type RelativeDate = {
  menuLabel: string;
  filterLabel?: string;
  format?: (timeZone: string) => void;
};

const getTimeText = (start: DateType, end: DateType, preset: string) => {
  if (preset === CUSTOM_RANGE) {
    if (start && end) {
      return `between ${start.format('MM/DD/YY')} - ${end.format('MM/DD/YY')}`;
    } else {
      // maintain the 'all time' filter label when a user switches from 'all time' to 'custom range'
      return 'for all time';
    }
  }
  const presetLabel = (RELATIVE_DATE_PRESETS_WITH_ALL_TIME as Record<string, RelativeDate>)[preset]
    .filterLabel;
  return presetLabel === RELATIVE_DATE_PRESETS_WITH_ALL_TIME.ALL_TIME.filterLabel
    ? 'for all time'
    : presetLabel;
};

export const EventDatePicker = ({ isDisabled }: EventDatePickerProps) => {
  const { dateRange, setDateRange } = useEventFilterContext();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const { startDate, endDate } = dateRange;

  const initialPreset = useMemo(() => (startDate && endDate ? CUSTOM_RANGE : ALL_TIME), []);
  const [preset, setPreset] = useState(initialPreset);

  const containerRef = useRef(null);
  useClickAway(containerRef, () => {
    setShowDatePicker(false);
  });

  const handleTogglePicker = () => setShowDatePicker((current) => !current);

  return (
    <DatePickerContainer ref={containerRef} data-test="event-date-picker">
      <DateTriggerButton
        onClick={handleTogglePicker}
        disabled={isDisabled}
        data-test="date-trigger-button"
      >
        <TimeText data-test="time-text">{getTimeText(startDate, endDate, preset)}</TimeText>
        <ChevronDown height={16} />
      </DateTriggerButton>
      {showDatePicker && (
        <DatePickerWrapper data-test="date-picker-wrapper">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            dateRangePreset={preset}
            defaultTimeZone={guessCurrentTimeZone()}
            onDateRangeChange={setDateRange}
            onPresetChange={(newPreset) => setPreset(newPreset)}
            presets={RELATIVE_DATE_PRESETS_WITH_ALL_TIME}
            promoteCustomInput
            blockFutureDates
            hasAllTimePreset
            showRelativeDatePresets
          />
        </DatePickerWrapper>
      )}
    </DatePickerContainer>
  );
};
